import React from "react";
import {doppeActionTypeDefineViewerMetadata} from "../../client-server-common/doppe-action-types/doppe-action-type";
import {doppeActionTypeLink} from "./doppe-action-type-link";
import {
    DoppeActionCTATypeLinkOpenType,
    doppeLinkSpecIsEntity,
    doppeLinkSpecIsPage,
    doppeLinkSpecIsUrl,
    DoppeLinkSpecEntityType,
} from "../../client-server-common/types/doppe-action-cta-button";
import {DoppeViewerModalPanelLayout} from "../../components/doppe-viewer-modal-panel/doppe-viewer-modal-panel";
import {
    doppeViewerActionContextOpenModal,
    doppeViewerActionContextOpenUrlInIframeModal
} from "../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context-utils";
import {
    DoppeActionModalIframeContent
} from "../../components/doppe-action-modal-iframe-content/doppe-action-modal-iframe-content";
import type {IDoppeViewerActionContext} from "../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context";
import {BymoPageViewerMode} from "../../components/bymo-page-viewer-context/bymo-page-viewer-context";
import {DoppeViewerLang} from "../../client-server-common/doppe-viewer/doppe-viewer-lang";
import {
    viewerPageNavigationNavigateToPage,
    viewerPageNavigationNavigateToEntity
} from "../../client-server-common/viewer-pages/viewer-page-navigation/viewer-page-navigation";

function navigateToPageViaRouter(
    pageId: string,
    actionContext: IDoppeViewerActionContext<any>
) {
    actionContext.performContextCallback(async ({clientServices, viewerContextData, bymoPageProps}) => {
        const router = clientServices.viewerRouterClientService;
        const bymoPageContextProps = actionContext.getBymoPageContextProps();
        const isLivePreviewMode = viewerContextData.mode === BymoPageViewerMode.LivePreview;

        viewerPageNavigationNavigateToPage(pageId, {
            router: router,
            bymoPageProps: bymoPageProps,
            bymoPageContextProps: bymoPageContextProps,
            viewerLang: DoppeViewerLang.English,
            showMobileView: true,
            isLivePreviewMode: isLivePreviewMode,
            handleExternalUrl: (externalUrl: string) => {
                if (isLivePreviewMode) {
                    doppeViewerActionContextOpenUrlInIframeModal(actionContext, externalUrl);
                } else {
                    window.location.href = externalUrl;
                }
            },
        });
    });
}

function navigateToEntityViaRouter(
    entityType: DoppeLinkSpecEntityType,
    entityId: string,
    actionContext: IDoppeViewerActionContext<any>
) {
    actionContext.performContextCallback(async ({clientServices, viewerContextData}) => {
        const router = clientServices.viewerRouterClientService;
        const bymoPageContextProps = actionContext.getBymoPageContextProps();
        const isLivePreviewMode = viewerContextData.mode === BymoPageViewerMode.LivePreview;

        viewerPageNavigationNavigateToEntity(entityType, entityId, {
            router: router,
            bymoPageContextProps: bymoPageContextProps,
            viewerLang: DoppeViewerLang.English,
            showMobileView: true,
            isLivePreviewMode: isLivePreviewMode,
            handleExternalUrl: (externalUrl: string) => {
                if (isLivePreviewMode) {
                    doppeViewerActionContextOpenUrlInIframeModal(actionContext, externalUrl);
                } else {
                    window.location.href = externalUrl;
                }
            },
        });
    });
}

export const doppeActionTypeLinkViewer = doppeActionTypeDefineViewerMetadata(doppeActionTypeLink, {
    getActionButtonProps: actionContext => {

        const action = actionContext.action;
        const url = action.settings.url;
        const openType = action.settings.openType;
        
        if (doppeLinkSpecIsPage(url)) {
            const pageId = url.pageId;
            if (pageId) {
                return {
                    onClick: () => {
                        navigateToPageViaRouter(pageId, actionContext);
                    }
                };
            }
        }
        
        if (doppeLinkSpecIsEntity(url)) {
            const entityType = url.entityType;
            const entityId = url.entityId;
            if (entityType && entityId) {
                return {
                    onClick: () => {
                        navigateToEntityViaRouter(entityType, entityId, actionContext);
                    }
                };
            }
        }
        
        if (doppeLinkSpecIsUrl(url)) {
            if (openType === DoppeActionCTATypeLinkOpenType.InPage) {
                return {
                    onClick: () => {
                        doppeViewerActionContextOpenModal(
                            actionContext,
                            () => ({
                                render: () => (
                                    <DoppeActionModalIframeContent
                                        withMinHeight={false}
                                        src={url}
                                    />
                                ),
                                dependencies: []
                            }),
                            {
                                mobileLayout: DoppeViewerModalPanelLayout.InnerFull,
                                desktopLayout: DoppeViewerModalPanelLayout.InnerFull,
                                showHeader: true,
                                showHeaderAsCloseButton: false,
                                title: action.name
                            }
                        );
                    }
                };
            }
            
            const target = openType === DoppeActionCTATypeLinkOpenType.NewTab ? '_blank' : '_self';
            
            return {
                href: url,
                target: target,
                rel: openType === DoppeActionCTATypeLinkOpenType.NewTab ? 'noopener' : undefined,
            };
        }

        return {};
    },
    
    activateCTA: actionContext => {
        const action = actionContext.action;
        const url = action.settings.url;
        const openType = action.settings.openType;
        
        if (doppeLinkSpecIsPage(url)) {
            const pageId = url.pageId;
            if (pageId) {
                navigateToPageViaRouter(pageId, actionContext);
            }
            return;
        }
        
        if (doppeLinkSpecIsEntity(url)) {
            const entityType = url.entityType;
            const entityId = url.entityId;
            if (entityType && entityId) {
                navigateToEntityViaRouter(entityType, entityId, actionContext);
            }
            return;
        }
        
        if (doppeLinkSpecIsUrl(url)) {
            if (openType === DoppeActionCTATypeLinkOpenType.InPage) {
                doppeViewerActionContextOpenModal(
                    actionContext,
                    () => ({
                        render: () => (
                            <DoppeActionModalIframeContent
                                withMinHeight={false}
                                src={url}
                            />
                        ),
                        dependencies: []
                    }),
                    {
                        mobileLayout: DoppeViewerModalPanelLayout.InnerFull,
                        desktopLayout: DoppeViewerModalPanelLayout.InnerFull,
                        showHeader: true,
                        showHeaderAsCloseButton: false,
                        title: action.name
                    }
                );
                return;
            }
            
            const newTab = openType === DoppeActionCTATypeLinkOpenType.NewTab;
            actionContext.navigateToUrl(url, newTab);
        }
    }
})
